import React, { useState, useEffect } from 'react';
import { FaSave, FaEnvelope, FaBell } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { applySnowPreference, getStoredSnowPreference } from '../utils/theme';

const IndstillingerGenerelt = () => {
  const [signature, setSignature] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [themeSettings, setThemeSettings] = useState({ auto_start_hour: 19, auto_end_hour: 6 });
  const [savingTheme, setSavingTheme] = useState(false);
  const [snowEnabled, setSnowEnabled] = useState(getStoredSnowPreference());
  const [pushMessage, setPushMessage] = useState('');
  const [pushStatus, setPushStatus] = useState('');
  const [pushLoading, setPushLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) return;
      try {
        const res = await fetch('/api/auth/verify', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data?.user?.id) {
          setCurrentUserId(data.user.id);
        }
      } catch (error) {
        console.error('Kunne ikke hente brugerinfo til push', error);
      }
    };
    fetchUser();
  }, [token]);

  const fetchData = async () => {
    try {
      const [signatureResponse, themeResponse] = await Promise.all([
        fetch('/api/indstillinger/signature'),
        fetch('/api/indstillinger/theme')
      ]);
      
      if (signatureResponse.ok) {
        const signatureData = await signatureResponse.json();
        setSignature(signatureData.signature || '');
      }
      
      if (themeResponse.ok) {
        const themeData = await themeResponse.json();
        if (typeof themeData.auto_start_hour === 'number' && typeof themeData.auto_end_hour === 'number') {
          setThemeSettings({
            auto_start_hour: themeData.auto_start_hour,
            auto_end_hour: themeData.auto_end_hour
          });
        }
        if (typeof themeData.snow_enabled === 'boolean') {
          setSnowEnabled(themeData.snow_enabled);
          applySnowPreference(themeData.snow_enabled);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    applySnowPreference(snowEnabled);
  }, [snowEnabled]);

  const handleSaveSignature = async () => {
    try {
      const response = await fetch('/api/indstillinger/signature', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signature }),
      });

      if (response.ok) {
        setMessage('Signaturen blev gemt!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      setMessage('Fejl ved gemning af signatur');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setMessage('Indtast venligst en email-adresse');
      return;
    }

    try {
      const response = await fetch('/api/indstillinger/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: testEmail }),
      });

      if (response.ok) {
        setMessage(`Test-email sendt til ${testEmail}!`);
        setTestEmail('');
      } else {
        const errorData = await response.json();
        setMessage(`Fejl: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setMessage('Fejl ved afsendelse af test-email');
    }
    
    setTimeout(() => setMessage(''), 5000);
  };

  // Mekanikere er fjernet fra denne side

  const handleSaveTheme = async () => {
    try {
      setSavingTheme(true);
      const response = await fetch('/api/indstillinger/theme', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_start_hour: Number(themeSettings.auto_start_hour),
          auto_end_hour: Number(themeSettings.auto_end_hour),
          snow_enabled: !!snowEnabled
        })
      });
      if (response.ok) {
        setMessage('Tema-indstillinger gemt!');
      } else {
        const data = await response.json();
        setMessage(`Fejl: ${data.message || 'Kunne ikke gemme tema'}`);
      }
    } catch (e) {
      setMessage('Fejl ved gemning af tema-indstillinger');
    } finally {
      setSavingTheme(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleToggleChristmas = async (enabled) => {
    setSnowEnabled(enabled);
    applySnowPreference(enabled);
    setMessage(enabled ? 'Sneeffekt aktiveret' : 'Sneeffekt slået fra');

    try {
      await fetch('/api/indstillinger/theme', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_start_hour: Number(themeSettings.auto_start_hour),
          auto_end_hour: Number(themeSettings.auto_end_hour),
          snow_enabled: !!enabled
        })
      });
    } catch (error) {
      console.error('Error saving snow preference', error);
    }

    setTimeout(() => setMessage(''), 4000);
  };

  const requestPushPermission = async () => {
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return Notification.requestPermission();
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const handleTestNotification = async () => {
    setPushMessage('');
    setPushStatus('');
    if (!currentUserId) {
      setPushStatus('Kunne ikke finde bruger-id. Log ind igen.');
      return;
    }
    if (!('Notification' in window)) {
      setPushStatus('Denne browser understøtter ikke notifikationer.');
      return;
    }
    if (!('serviceWorker' in navigator)) {
      setPushStatus('Service worker understøttes ikke i denne browser.');
      return;
    }

    setPushLoading(true);
    try {
      const permission = await requestPushPermission();
      if (permission !== 'granted') {
        setPushStatus('Notifikationer er ikke tilladt i browseren.');
        return;
      }

      const publicKeyRes = await fetch('/api/push/public-key');
      const publicKeyData = await publicKeyRes.json();
      if (!publicKeyRes.ok || !publicKeyData?.publicKey) {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification('Test notifikation', {
          body: 'VAPID public key mangler. Dette er en lokal test i browseren.',
          icon: '/favicon.svg',
          badge: '/favicon.svg',
          data: { url: '/indstillinger' }
        });
        setPushStatus('VAPID public key mangler. Kontakt en administrator.');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKeyData.publicKey)
        });
      }

      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: subscription.toJSON().keys,
          user_id: currentUserId
        })
      });

      const sendRes = await fetch('/api/push/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'Test notifikation',
          body: 'Dette er en test af push-notifikationer fra Indstillinger.',
          url: '/indstillinger',
          recipients: [currentUserId]
        })
      });
      const sendData = await sendRes.json();
      if (!sendRes.ok) {
        throw new Error(sendData?.message || 'Kunne ikke sende test-notifikation');
      }
      setPushMessage('Test-notifikation sendt. Tjek dine notifikationer.');
    } catch (error) {
      console.error('Push test fejl:', error);
      setPushStatus(error.message || 'Kunne ikke sende test-notifikation');
    } finally {
      setPushLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  const quillModules = {
    toolbar: [
      [{ 'font': [] }],
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Generelle Indstillinger</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">Administrer systemets generelle indstillinger</p>
      </div>

      {message && (
        <div className="mb-4 p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded">
          {message}
        </div>
      )}

      {/* Tema: Auto mørk tidsrum */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Tema</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">Definér hvornår “Auto” viser mørkt tema.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start time (0-23)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={themeSettings.auto_start_hour}
              onChange={(e) => setThemeSettings(s => ({ ...s, auto_start_hour: e.target.value }))}
              className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Slut time (0-23)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={themeSettings.auto_end_hour}
              onChange={(e) => setThemeSettings(s => ({ ...s, auto_end_hour: e.target.value }))}
              className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue"
            />
          </div>
        </div>
        <button
          onClick={handleSaveTheme}
          disabled={savingTheme}
          className="bg-falck-blue disabled:opacity-60 text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors flex items-center"
        >
          <FaSave className="mr-2" />
          Gem Tema
        </button>
        <div className="mt-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">Juletema</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Tilføj faldende sne ovenpå det valgte lys/mørk tema.</p>
            </div>
            <label className="inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                className="sr-only"
                checked={snowEnabled}
                onChange={(e) => handleToggleChristmas(e.target.checked)}
              />
              <span className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 dark:bg-gray-700 transition-colors">
                <span
                  className={`absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transform transition-transform ${snowEnabled ? 'translate-x-5' : ''}`}
                ></span>
              </span>
            </label>
          </div>
          {snowEnabled && (
            <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">Sneeffekten er aktiveret. Slå den fra for at vende tilbage til standard visning.</p>
          )}
        </div>
      </div>

      {/* Email Signatur */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Email Signatur</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            HTML Signatur
          </label>
          <div className="bg-white">
            <ReactQuill
              theme="snow"
              value={signature}
              onChange={setSignature}
              modules={quillModules}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">Tip: Indsæt billeder via værktøjslinjen. De lagres som base64 i HTML.</p>
        </div>
        <button
          onClick={handleSaveSignature}
          className="bg-falck-blue text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors flex items-center"
        >
          <FaSave className="mr-2" />
          Gem Signatur
        </button>
      </div>

      {/* Test Email */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Test Email</h2>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Adresse
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue"
              placeholder="test@example.com"
            />
          </div>
          <button
            onClick={handleSendTestEmail}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <FaEnvelope className="mr-2" />
            Send Test Email
          </button>
        </div>
      </div>

      {/* Test Push-notifikation */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Test notifikation</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Send en test-notifikation til din bruger for at bekræfte at browseren modtager push-beskeder.
        </p>
        {pushStatus && (
          <div className="mb-3 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
            {pushStatus}
          </div>
        )}
        {pushMessage && (
          <div className="mb-3 p-3 bg-green-100 border border-green-300 text-green-700 rounded text-sm">
            {pushMessage}
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            onClick={handleTestNotification}
            disabled={pushLoading}
            className="bg-falck-blue disabled:opacity-60 text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors flex items-center"
          >
            <FaBell className="mr-2" />
            {pushLoading ? 'Sender...' : 'Send test-notifikation'}
          </button>
          <span className="text-xs text-gray-500">
            Browser tilladelse: {typeof Notification !== 'undefined' ? Notification.permission : 'ukendt'}
          </span>
        </div>
      </div>

      
    </div>
  );
};

export default IndstillingerGenerelt;
